/* eslint-disable camelcase */
/* eslint-disable fp/no-mutation */
import { Button } from '@nike/eds';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setNposValue } from '../../redux/actions';
import { getBodyTranslations } from '../../redux/selectors';
import { NPOS, NPOS_APP } from '../../utils/constants';
import { getApplicationSource, getApplicationSourceName } from '../../utils/iframeMessaging';
import { eventCaptured } from '../../utils/service-calls';
import { EJ_NPOS_REQUEST, postWkMessage } from '../../utils/webkitMessaging';
import {
  ButtonBlack,
  ButtonWhite
} from '../reusable';

import { isNposReprintEnabled } from './Npos/config/npos-config';
import { checkReprintAvailability } from './Npos/nposUtils';
import { PrintIcon, ReturnIcon, WarningIcon } from './Npos/reusable/icons';
import PrintReceiptInfo from './PrintReceiptInfo';

const EVENT_TYPES = {
  PRINT: 'print',
  REPRINT: 'reprint',
  RETURN: 'return'
};

const ReceiptViewActions = ({
  isNpos, showNewUi, storeInfo, transactionBlob, authToken
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const printJobInfo = useSelector((state) => state.nposValues.printJobInfo);

  const dispatch = useDispatch();

  const isPrintingInProgress = printJobInfo?.status === NPOS.PRINT_STATUS.INITIATED;

  const { RL_PrintEJ, RL_StartReturn, RL_PrintReceipt } = useSelector(getBodyTranslations);

  const setIsPrinterDialogOpen = (value) => dispatch(setNposValue({ isPrinterDialogOpen: value }));

  const openPrintDialog = () => (getApplicationSource() === NPOS_APP ? postWkMessage(EJ_NPOS_REQUEST.PRINT_RECEIPT) : window.print());

  const reprintAvailability = checkReprintAvailability(transactionBlob?.Transaction);
  const isReprintEnabled = isNposReprintEnabled(storeInfo?.selectedCountry?.value);

  const captureEvent = async (eventType, additionalDetails = {}) => {
    const logDetail = {
      company: storeInfo.selectedStore?.full.company.toUpperCase(),
      country: storeInfo.selectedCountry.value,
      eventType,
      source: getApplicationSourceName(),
      storeNumber: storeInfo.selectedStore.full.storeNumber,
      ...additionalDetails
    };
    await eventCaptured(storeInfo.selectedCountry?.value, logDetail, authToken);
  };

  const onPrintClickBoh = async () => {
    openPrintDialog();
    captureEvent(EVENT_TYPES.PRINT);
  };

  const onPrintClickFoh = async () => {
    setIsPrinterDialogOpen(true);
    captureEvent(EVENT_TYPES.REPRINT);
  };

  const onReturnClick = () => {
    const orderId = transactionBlob?.Transaction?.RetailTransaction?.DigitalOrderID || transactionBlob?.Transaction?.OrderTransaction?.DigitalOrderID;
    postWkMessage(EJ_NPOS_REQUEST.RETURN, orderId);
    captureEvent(EVENT_TYPES.RETURN, { orderId });
  };

  return (
    <div className="receipt-view-actions-container">
      {showNewUi ? (
        <div>
          <div className="receipt-view-actions">
            {isReprintEnabled && (
              <div className="action-print-container d-sm-flx flx-ai-sm-c">
                <PrintReceiptInfo reprintAvailability={reprintAvailability} setShowInfo={setShowInfo} showInfo={showInfo}>
                  <Button
                    beforeSlot={!reprintAvailability?.isReprintAvailable ? <WarningIcon size="m" /> : <PrintIcon size="m" />}
                    disabled={isPrintingInProgress}
                    variant="secondary"
                    onClick={reprintAvailability?.isReprintAvailable ? onPrintClickFoh : () => setShowInfo(true)}
                  >
                    {RL_PrintReceipt}
                  </Button>
                </PrintReceiptInfo>
              </div>
            )}
            <Button beforeSlot={<ReturnIcon size="m" />} onClick={onReturnClick}>{RL_StartReturn}</Button>
          </div>
        </div>
      ) : (
        <div className="receipt-view-actions">
          {isNpos ? (
            <>
              {isReprintEnabled && (
                <div className="action-print-container d-sm-flx flx-ai-sm-c">
                  <PrintReceiptInfo reprintAvailability={reprintAvailability} setShowInfo={setShowInfo} showInfo={showInfo}>
                    <ButtonWhite
                      beforeSlot={!reprintAvailability?.isReprintAvailable ? <WarningIcon size="s" /> : <PrintIcon size="s" />}
                      className="va-sm-t mt2-sm mb2-sm hide-print"
                      disabledStyle={!reprintAvailability?.isReprintAvailable}
                      isDisabled={isPrintingInProgress}
                      label={RL_PrintReceipt}
                      onClick={reprintAvailability?.isReprintAvailable ? onPrintClickFoh : () => setShowInfo(true)}
                    />
                  </PrintReceiptInfo>
                </div>
              )}
              <ButtonBlack
                beforeSlot={<ReturnIcon size="s" />}
                className="va-sm-t mt2-sm mb2-sm hide-print"
                label={RL_StartReturn}
                onClick={() => onReturnClick()}
              />
            </>
          ) : (
            <ButtonWhite
              className="va-sm-t mt2-sm mb2-sm hide-print"
              label={RL_PrintEJ}
              onClick={onPrintClickBoh}
            />
          )}
        </div>
      )}
    </div>
  );
};

ReceiptViewActions.propTypes = {
  authToken: PropTypes.string.isRequired,
  isNpos: PropTypes.bool.isRequired,
  showNewUi: PropTypes.bool.isRequired,
  storeInfo: PropTypes.shape().isRequired,
  transactionBlob: PropTypes.shape().isRequired
};

export default ReceiptViewActions;
